import React, { useState, useEffect } from "react";
import { firestoreService } from "@/services/firestoreService";

interface TickerItem {
  symbol: string;
  price: number;
  change: number;
  changePercent: number;
  name?: string;
}

export const MarketTicker: React.FC = () => {
  const [tickers, setTickers] = useState<TickerItem[]>([]);

  useEffect(() => {
    loadTickers();
    // Refresh every 30 seconds
    const interval = setInterval(loadTickers, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadTickers = async () => {
    try {
      // Get market movers (gainers and losers) for ticker
      const [gainers, losers] = await Promise.all([
        firestoreService.getDocuments<TickerItem>("marketMovers", [
          firestoreService.where("type", "==", "gainer"),
          firestoreService.orderBy("changePercent", "desc"),
          firestoreService.limit(10),
        ]).catch(() => []),
        firestoreService.getDocuments<TickerItem>("marketMovers", [
          firestoreService.where("type", "==", "loser"),
          firestoreService.orderBy("changePercent", "asc"),
          firestoreService.limit(10),
        ]).catch(() => []),
      ]);

      // Combine and format for ticker
      const allTickers: TickerItem[] = [
        ...gainers.map((item: any) => ({
          symbol: item.ticker || item.symbol || "N/A",
          price: item.price || item.currentPrice || 0,
          change: item.change || item.changePercent || 0,
          changePercent: item.changePercent || 0,
          name: item.companyName || item.name,
        })),
        ...losers.map((item: any) => ({
          symbol: item.ticker || item.symbol || "N/A",
          price: item.price || item.currentPrice || 0,
          change: item.change || item.changePercent || 0,
          changePercent: item.changePercent || 0,
          name: item.companyName || item.name,
        })),
      ];

      // If no market movers, use some default popular tickers
      if (allTickers.length === 0) {
        const defaultTickers = [
          { symbol: "AAPL", price: 0, change: 0, changePercent: 0 },
          { symbol: "MSFT", price: 0, change: 0, changePercent: 0 },
          { symbol: "GOOGL", price: 0, change: 0, changePercent: 0 },
          { symbol: "AMZN", price: 0, change: 0, changePercent: 0 },
          { symbol: "TSLA", price: 0, change: 0, changePercent: 0 },
          { symbol: "NVDA", price: 0, change: 0, changePercent: 0 },
        ];
        setTickers(defaultTickers);
      } else {
        setTickers(allTickers);
      }
    } catch (error) {
      console.error("Failed to load tickers:", error);
      // Fallback to default tickers
      const defaultTickers = [
        { symbol: "AAPL", price: 0, change: 0, changePercent: 0 },
        { symbol: "MSFT", price: 0, change: 0, changePercent: 0 },
        { symbol: "GOOGL", price: 0, change: 0, changePercent: 0 },
      ];
      setTickers(defaultTickers);
    }
  };

  // Always show ticker, even if loading (with default tickers)
  const displayTickers = tickers.length > 0 ? tickers : [
    { symbol: "AAPL", price: 0, change: 0, changePercent: 0 },
    { symbol: "MSFT", price: 0, change: 0, changePercent: 0 },
    { symbol: "GOOGL", price: 0, change: 0, changePercent: 0 },
    { symbol: "AMZN", price: 0, change: 0, changePercent: 0 },
    { symbol: "TSLA", price: 0, change: 0, changePercent: 0 },
    { symbol: "NVDA", price: 0, change: 0, changePercent: 0 },
  ];

  return (
    <div className="bg-gray-900 text-white overflow-hidden relative h-10">
      <div className="flex animate-scroll" style={{ width: 'fit-content' }}>
        {/* Duplicate items for seamless loop */}
        {[...displayTickers, ...displayTickers, ...displayTickers].map((ticker, index) => (
          <div
            key={`${ticker.symbol}-${index}`}
            className="flex items-center gap-4 px-6 py-2 whitespace-nowrap"
          >
            <span className="font-mono font-bold text-sm">{ticker.symbol}</span>
            {ticker.price > 0 && (
              <span className="text-sm">${ticker.price.toFixed(2)}</span>
            )}
            <span
              className={`text-sm font-semibold ${
                ticker.changePercent >= 0 ? "text-green-400" : "text-red-400"
              }`}
            >
              {ticker.changePercent >= 0 ? "+" : ""}
              {ticker.changePercent.toFixed(2)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

